import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge } from '@ibe/ui';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { AuditTrailPanel } from '../../../components/resource-detail/audit-trail-panel.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { usePaymentGateway } from '../api/queries.js';
import { useSoftDeletePaymentGateway } from '../api/mutations.js';
import { type PaymentGatewayDetail } from '../api/schemas.js';

function activeTone(active: boolean): 'success' | 'neutral' {
  return active ? 'success' : 'neutral';
}

function formatInstant(value: string): string {
  return new Date(value).toLocaleString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PaymentGatewayDetailPage(): React.JSX.Element {
  const { gatewayId = '' } = useParams();
  const navigate = useNavigate();
  const query = usePaymentGateway(gatewayId);
  const softDelete = useSoftDeletePaymentGateway();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const onDelete = async (): Promise<void> => {
    await softDelete.mutateAsync(gatewayId);
    setConfirmingDelete(false);
    void navigate('/payment-gateways');
  };

  return (
    <ResourceDetailPage<PaymentGatewayDetail>
      query={query}
      permission={PERMISSIONS.PAYMENT_READ}
      parent={{ to: '/payment-gateways', label: 'Payment gateways' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.PAYMENT_CONFIGURE}>
            <Button asChild variant="secondary">
              <Link to={`/payment-gateways/${gatewayId}/edit`}>
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Edit
              </Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.PAYMENT_CONFIGURE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <DetailGrid>
            <DetailField label="Provider">{data.provider}</DetailField>
            <DetailField label="Status">
              <StatusBadge tone={activeTone(data.isActive)}>
                {data.isActive ? 'Active' : 'Inactive'}
              </StatusBadge>
            </DetailField>
            <DetailField label="Default">{data.isDefault ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Credentials">
              {data.credentialsConfigured ? 'Configured' : 'Not configured'}
            </DetailField>
            <DetailField label="Tenant">{data.tenantId}</DetailField>
            <DetailField label="Created">{formatInstant(data.createdAt)}</DetailField>
            <DetailField label="Updated">{formatInstant(data.updatedAt)}</DetailField>
          </DetailGrid>

          <AuditTrailPanel entityType="payment_gateway" entityId={data.id} />

          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete gateway"
            description={`${data.name} will be removed. Its audit history is retained.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              void onDelete();
            }}
          />
        </>
      )}
    </ResourceDetailPage>
  );
}
